import React from "react";
import { Link } from "react-router-dom";
import useUserStore from "../stores/useUserStore";
import { buttonClass, IconButton } from "./ui/Button";
import Tooltip from "./ui/Tooltip";
import { IconClose } from "./ui/icons";

/**
 * SlideMenu - Unified slide-in menu component
 *
 * @param {boolean} isOpen - Whether the menu is visible
 * @param {function} onClose - Callback to close the menu
 * @param {Array} items - Menu items array
 * @param {boolean} showLogout - Whether to show logout button
 *
 * Item shape:
 * {
 *   to: string,           // Route path or external URL
 *   label: string,        // Display text
 *   indent?: boolean,     // Indent as sub-item
 *   external?: boolean,   // Open in new tab
 *   adminOnly?: boolean,  // Only show for admin users
 * }
 */
const SlideMenu = ({ isOpen, onClose, items = [], showLogout = false }) => {
  const user = useUserStore((state) => state.user);
  const logout = useUserStore((state) => state.logout);

  const hasRole = user?.isadmin || user?.can_admin || user?.can_operations || user?.can_manufacturing || user?.can_producer || user?.can_sales;

  // Filter items based on access requirements
  const visibleItems = items.filter((item) => {
    if (item.adminOnly && !user?.isadmin) return false;
    if (item.requiresRole && !hasRole) return false;
    return true;
  });

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Menu panel */}
      <div
        className={`fixed top-0 right-0 z-50 bg-gray-900/95 backdrop-blur-md
          border-l border-gray-800 shadow-[-4px_0_24px_rgba(0,0,0,0.5)]
          transition-transform duration-300 ease-out
          flex flex-col h-screen [height:100dvh]
          ${isOpen ? "translate-x-0" : "translate-x-full"}
          w-64 max-w-[85vw]`}
        role="navigation"
      >
        {/* Header with close button */}
        <div className="flex shrink-0 items-center justify-between p-4 border-b border-gray-800">
          <span className="text-on-surface-variant text-xs uppercase tracking-wider">Menu</span>
          <Tooltip label="Close menu">
          <IconButton label="Close menu" onClick={onClose}>
            <IconClose />
          </IconButton>
          </Tooltip>
        </div>

        {/* Menu items */}
        <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col py-2">
          {visibleItems.map((item, index) => {
            const baseClasses = `
              block px-6 py-3 text-sm font-semibold tracking-wide
              transition-all duration-200
              hover:text-primary hover:drop-shadow-[0_0_8px_rgba(255,204,51,0.6)]
              ${item.indent ? "pl-10 text-on-surface-variant text-xs" : "text-white"}
            `;

            if (item.external) {
              return (
                <a
                  key={index}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={baseClasses}
                  onClick={onClose}
                >
                  {item.label}
                  <span className="ml-2 text-on-surface-disabled text-xs">↗</span>
                </a>
              );
            }

            if (item.onClick) {
              return (
                <button
                  key={index}
                  className={baseClasses + " text-left w-full"}
                  onClick={() => { item.onClick(); onClose(); }}
                >
                  {item.label}
                </button>
              );
            }

            return (
              <Link
                key={index}
                to={item.to}
                className={baseClasses}
                onClick={onClose}
              >
                {item.indent && <span className="mr-1">↳</span>}
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Logout button at bottom */}
        {showLogout && (
          <div className="shrink-0 p-4 border-t border-gray-800">
            <button
              onClick={() => {
                logout();
                onClose();
              }}
              className={`${buttonClass('danger', 'lg')} w-full`}
            >
              Log Out
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default SlideMenu;
